import * as React from 'react';

import HighlightedText, { HighlightedSegments } from '../HighlightedText';
import InspectLink from '../InspectLink';

import { splitIntoInspectLinkedRuns } from './inspectLinkedRuns';

export interface IInspectLinkedTextProps {
  text: string;
  /** Active search term; hits stay marked, including inside a linked id. */
  query?: string | null;
}

/**
 * Renders `text` with every id in it — a whole uuid or the eight-character
 * short form — linked to the object inspector, and every search hit marked.
 * With no id it renders exactly what {@link HighlightedText} would, so it is
 * safe to use in place of one.
 *
 * Never mount it inside a `<button>`: an anchor there is invalid HTML, and the
 * two click targets fight. In a card that expands on click, the collapsed
 * preview is the button and this belongs in the expanded body.
 *
 * Hook-free for the same reason as `HighlightedText` — a transcript renders
 * thousands of these.
 */
const InspectLinkedText = ({ text, query }: IInspectLinkedTextProps): React.ReactElement => {
  const runs = splitIntoInspectLinkedRuns(text, query);
  if (runs === null) {
    return <HighlightedText text={text} query={query} />;
  }
  return (
    <>
      {runs.map((run, i) =>
        run.id === null ? (
          <HighlightedSegments key={i} segments={run.segments} />
        ) : (
          <InspectLink key={i} id={run.id}>
            <HighlightedSegments segments={run.segments} />
          </InspectLink>
        )
      )}
    </>
  );
};

export default InspectLinkedText;
